import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {Button, Size, Type} from '@Components/button';
import styles from './publishing-share-to-app-panel.module.scss';
import {LOADER_SIZE} from '@Components/circular-progress-loader';
import {USER_PREMIUM_LEVELS} from '@Utils/user.util';
import {getUserPremiumLevel} from '@Libraries/user.library';
import {Text, TextSize} from '@Components/text';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2';
import {useFreebieProduct, useHashedId, usePrintProduct} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import {setErrorMessageForPublishingPanel} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import {PublishingLoaderScreen} from '@Panels/poster-publishing-options-panel/components/publishing-loader-screen/publishing-loader-screen';
import {Status} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {getAssetUrl} from '@Utils/s3.util';
import {useAppDispatch} from '@/hooks';
import type {AJAXisHighResResponse, ShareToAppCheckoutResponse} from './publishing-share-to-app-panel.types';
import {doWebShare, isNewPremiumDownload, onShareToApp, onWebShareError, ORDER_REGEN_RESPONSE, SHARE_TO_APP_UNDEFINED} from './publishing-share-to-app-library';
import {PageSelectList} from '@Components/page-select-list/page-select-list';
import type {PosterPagePreviewDataResponse, PosterPagePreviewInfo} from '@Components/poster-preview-container/poster-preview-container.types';

export function PublishingShareToAppPanel(): ReactElement<any> {
  const [isLoading, setIsLoading] = useState(false);
  const [isHighRes, setIsHighRes] = useState(false);
  const [idProduct, setIdProduct] = useState(0);
  const freebieProduct = useFreebieProduct();
  const printProduct = usePrintProduct();
  const [removeWatermark, setRemoveWatermark] = useState(false);
  const productUsed = printProduct?.needsPurchase ? freebieProduct : printProduct;
  const posterHashedId = useHashedId();
  const [filesToShare, setFilesToShare] = useState<File[]>([]);
  const [canShare, setCanShare] = useState(false);
  const dispatch = useAppDispatch();
  const orderId = printProduct?.orderId;
  const userPremiumLevel = getUserPremiumLevel();
  const readyImage = getAssetUrl('publishing-design-ready.png');
  const [posterPageIds, setPosterPageIds] = useState<string[]>([]);
  const [thumbnailsArray, setThumbnailsArray] = useState<string[]>([]);
  const [pagesLoading, setPagesLoading] = useState(true);
  const [selectedPages, setSelectedPages] = useState<string[]>([]);

  useEffect(() => {
    void canUserShareHighRes();
    void fetchPageData();
  }, [posterHashedId]);

  const canUserShareHighRes = async (): Promise<void> => {
    if (!isShareToAppAvailable()) {
      return;
    }
    try {
      setIsLoading(true);
      const response = (await window.PMW.readLocal('socialmedia/canUserShareHighRes', {hashedId: posterHashedId})) as AJAXisHighResResponse;
      if (response) {
        setIdProduct(Number(response.idproduct));
        setIsHighRes(response.highres);
      }
    } catch {
      onWebShareError();
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPageData = async (): Promise<void> => {
    try {
      const response = (await window.PMW.readLocal('poster/getPosterPagePreviewDataForCarousel', {
        posterHashedId,
      })) as PosterPagePreviewDataResponse;

      const pageIds = response.pagesInfo.map((page: PosterPagePreviewInfo) => page.pageHashedId);
      const thumbnails = response.pagesInfo.map((page: PosterPagePreviewInfo) => page.thumbnailUrl);

      setPosterPageIds(pageIds);
      setThumbnailsArray(thumbnails);
      setSelectedPages([...pageIds]);
    } catch {
      onWebShareError();
    } finally {
      setPagesLoading(false);
    }
  };

  const isShareToAppAvailable = (): boolean => {
    return (window.PMW.isMobileOS() && typeof navigator !== 'undefined' && navigator.canShare?.bind(navigator)) as boolean;
  };

  const onShareToAppFreeCorrection = (): void => {
    void window.PMW.writeLocal('cart/useFreeCorrectionNew', {
      idorder: orderId,
    })
      .then(doWebShareHelper)
      .catch(() => {
        onWebShareError();
      });
  };

  const onShareToAppCheckoutSuccess = (response: ShareToAppCheckoutResponse): void => {
    if (response.orderRegen) {
      doWebShareHelper();
      return;
    }
    parseResponseForCheckout(response);
  };

  const onShareToAppCheckout = (): void => {
    void window.PMW.writeLocal('cart/checkoutPublish', {
      idposter: posterHashedId,
      idproduct: idProduct,
      promotion: '',
      mode: 'share-to-app',
      phids: JSON.stringify(selectedPages),
    })
      .then((response: ShareToAppCheckoutResponse) => {
        onShareToAppCheckoutSuccess(response);
      })
      .catch(() => {
        onWebShareError();
      });
  };

  const processDesignForShareToApp = (): void => {
    setIsLoading(true);
    if (!removeWatermark && userPremiumLevel === USER_PREMIUM_LEVELS.PAYG) {
      doWebShareHelper();
    } else if (Number(orderId) !== 0 && userPremiumLevel === USER_PREMIUM_LEVELS.PAYG) {
      onShareToAppFreeCorrection();
    } else {
      onShareToAppCheckout();
    }
  };

  const newPremiumDowloadForShareToApp = (): void => {
    const downloadOptions = selectedPages.length > 0 ? {pagesHashedIds: selectedPages} : null;
    void window.PMW.writeLocal('cart/premiumDownload/', {
      productId: idProduct,
      hid: posterHashedId,
      is: 1,
      downloadOptions: downloadOptions,
      clientSource: 'publishing-share-to-app-panel:newPremiumDowloadForShareToApp',
      clientTrace: new Error().stack ?? '',
    })
      .then(doWebShareHelper)
      .catch(() => {
        dispatch(setErrorMessageForPublishingPanel(window.i18next.t('pmwjs_error_message')));
      });
  };

  const rawPriceForShareToApp = (response: ShareToAppCheckoutResponse): void => {
    window.PMW.util.require(typeof window.PMW.stripeCheckout === 'undefined', 'stripeCheckoutDialog', true, () => {
      window.PMW.postCheckoutProcessing(response, null, doWebShareHelper);
    });
  };

  const isOrderRegen = (response: ShareToAppCheckoutResponse): boolean => {
    return !!response.orderRegen && response.orderRegen === ORDER_REGEN_RESPONSE;
  };

  const doWebShareHelper = (): void => {
    doWebShare(productUsed, posterHashedId, isHighRes, setCanShare, setIsLoading, setFilesToShare, selectedPages);
  };

  const parseResponseForCheckout = (response: ShareToAppCheckoutResponse): void => {
    if (isOrderRegen(response)) {
      doWebShareHelper();
      return;
    }
    if (isNewPremiumDownload(response)) {
      newPremiumDowloadForShareToApp();
    } else if (response.status && response.status === Status.DOWNLOADABLE) {
      doWebShareHelper();
    } else if (response.message && response.message.length !== 0) {
      onWebShareError();
    } else if (typeof response.checkout['raw-price'] !== SHARE_TO_APP_UNDEFINED && response.checkout['raw-price'] > 0) {
      rawPriceForShareToApp(response);
    } else {
      doWebShareHelper();
    }
  };

  const onClickShareToApp = (): void => {
    if (canShare) {
      onShareToApp(productUsed, filesToShare, setIsLoading);
    } else {
      processDesignForShareToApp();
    }
  };

  const getReadyToShareUI = (): ReactElement => {
    return (
      <div>
        <div className="flex-center spacing-p-t-6 spacing-p-b-4 flex-v-row">
          <img className={styles.imageHeight} src={readyImage} alt="" />
          <Text size={TextSize.LARGE} className="content-body spacing-p-t-4 spacing-p-b-5" val={window.i18next.t('pmwjs_ready_to_share')} />
        </div>
        <div className="flex-center spacing-p-2">
          <Button onClick={onClickShareToApp} type={Type.PRIMARY} isFullWidth text={window.i18next.t('pmwjs_share_to_app')} />
        </div>
      </div>
    );
  };

  const buttonWithWatermarkToggle = (): ReactElement => {
    return (
      <div className={`flex-row-justify-between flex-items-center ${styles.footer}`}>
        <div className="flexbox flex-items-center">
          <ToggleSwitch
            className="spacing-m-r-2"
            id="example"
            size={ToggleSwitchV2Size.MEDIUM}
            isChecked={removeWatermark}
            onChange={() => {
              setRemoveWatermark((removeWatermark) => !removeWatermark);
            }}
          />
          <Text val={window.i18next.t('pmwjs_remove_watermark')} size={TextSize.XXSMALL} bold />
        </div>
        <Button type={Type.PRIMARY} size={Size.SMALL} text={window.i18next.t('pmwjs_share_to_app')} disabled={selectedPages.length === 0} onClick={onClickShareToApp} />
      </div>
    );
  };

  const buttonWithoutWatermarkToggle = (): ReactElement => {
    return (
      <div className={styles.footer}>
        <Button type={Type.PRIMARY} size={Size.SMALL} text={window.i18next.t('pmwjs_share_to_app')} disabled={selectedPages.length === 0} onClick={onClickShareToApp} isFullWidth />
      </div>
    );
  };

  const getPageSelectorUI = (): ReactElement => {
    return (
      <div className="flex-v-row spacing-p-4">
        <div className={`spacing-p-b-4 ${styles.pageListContainer}`}>
          <PageSelectList
            pageOrder={posterPageIds}
            selectedPages={selectedPages}
            onSelectedPagesChange={setSelectedPages}
            thumbnailsArray={thumbnailsArray}
            selectAllLabel={window.i18next.t('pmwjs_select_all_pages', {pages: posterPageIds.length})}
          />
        </div>
        {!isHighRes ? buttonWithWatermarkToggle() : buttonWithoutWatermarkToggle()}
      </div>
    );
  };

  const renderShareToApp = (): ReactElement => {
    if (pagesLoading || isLoading) {
      return <PublishingLoaderScreen customHeight={styles.setCustomHeight} loaderSize={LOADER_SIZE.LARGE} />;
    } else if (canShare) {
      return getReadyToShareUI();
    } else {
      return getPageSelectorUI();
    }
  };

  return renderShareToApp();
}
